/* eslint-disable no-unused-vars */
import React, { useMemo, useState, useEffect } from 'react';
import { PLATS, NM, COST_FIELDS, PRE_MONTHS } from '../utils/constants';
import { calcPlatform, calcRoyalties } from '../utils/calc';
import { getCostState, getDealTerms } from '../utils/stateFactory';
import { fu, mlSafe } from '../utils/format';
import supabase, { orgId } from '../supabaseClient';

export default function PnLOverview({
  games, PS, CS, DT, currentMonthIdx, startDate, functionalCurrency, convert, peopleOhTotal, peopleDevTotal,
  ohCategories, getCategoryMonthlyTotal,
}) {
  const fc  = functionalCurrency || 'USD';
  const now = currentMonthIdx ?? NM - 1;
  const [narrative, setNarrative] = useState('');
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    supabase.from('app_settings').select('value')
      .eq('org_id', orgId).eq('key', 'pnl_narrative').maybeSingle()
      .then(({ data }) => { if (data?.value) setNarrative(data.value); });
  }, []);

  const metrics = useMemo(() => {
    const activeGames = games.filter(g => PLATS.some(p => PS[g]?.[p.key]?.enabled));

    // Monthly revenue at current month
    let monthlyRev = 0;
    activeGames.forEach(g => {
      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        const v = calcPlatform(p.key, PS[g][p.key]).netRaw[now] ?? 0;
        monthlyRev += convert ? convert(v, fc, now) : v;
      });
    });

    // Monthly overhead at current month
    let monthlyOh = 0;
    activeGames.forEach(g => {
      const costs = getCostState(CS, g);
      ['recoup','nonRecoup'].forEach(sec => {
        COST_FIELDS.forEach(({ key }) => {
          const ai = now + PRE_MONTHS;
          monthlyOh += parseFloat(costs[sec][key][ai]) || 0;
        });
      });
    });
    // Add people costs
    monthlyOh += (peopleOhTotal?.[now] || 0) + (peopleDevTotal?.[now] || 0);

    const monthlyNet = monthlyRev - monthlyOh;
    // Runway: months until cash runs out at current burn
    const runway = monthlyNet < 0 && monthlyOh > 0 ? null : Math.round(monthlyRev / Math.max(monthlyOh, 1) * 12);

    // FY totals (first 12 months)
    const fyEnd = Math.min(11, NM - 1);
    let fyRevForecast = 0, fyRevToDate = 0;
    let fyOhForecast  = 0, fyOhToDate  = 0;

    activeGames.forEach(g => {
      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => {
          if (v !== null && i <= fyEnd) {
            const vFC = convert ? convert(v, fc, i) : v;
            fyRevForecast += vFC;
            if (i <= now) fyRevToDate += vFC;
          }
        });
      });
    });

    activeGames.forEach(g => {
      const costs = getCostState(CS, g);
      ['recoup','nonRecoup'].forEach(sec => {
        COST_FIELDS.forEach(({ key }) => {
          costs[sec][key].forEach((v, ai) => {
            const i = ai - PRE_MONTHS;
            if (i >= 0 && i <= fyEnd) {
              fyOhForecast += parseFloat(v) || 0;
              if (i <= now) fyOhToDate += parseFloat(v) || 0;
            }
          });
        });
      });
    });
    // Add people
    for (let i = 0; i <= fyEnd; i++) {
      fyOhForecast += (peopleOhTotal?.[i] || 0) + (peopleDevTotal?.[i] || 0);
      if (i <= now) fyOhToDate += (peopleOhTotal?.[i] || 0) + (peopleDevTotal?.[i] || 0);
    }

    // Dev advances this month and YTD
    let devThisMonth = 0, devYTD = 0, devForecast = 0;
    let mktThisMonth = 0, mktYTD = 0, mktForecast = 0;
    let pubThisMonth = 0, pubYTD = 0, pubForecast = 0;
    let royThisMonth = 0;
    activeGames.forEach(g => {
      const costs = getCostState(CS, g);
      ['recoup','nonRecoup'].forEach(sec => {
        costs[sec].devAdvances.forEach((v, ai) => {
          const i = ai - PRE_MONTHS;
          const n = parseFloat(v) || 0;
          if (i === now) devThisMonth += n;
          if (i >= 0 && i <= now) devYTD += n;
          if (i >= 0 && i <= fyEnd) devForecast += n;
        });
        costs[sec].marketing.forEach((v, ai) => {
          const i = ai - PRE_MONTHS;
          const n = parseFloat(v) || 0;
          if (i === now) mktThisMonth += n;
          if (i >= 0 && i <= now) mktYTD += n;
          if (i >= 0 && i <= fyEnd) mktForecast += n;
        });
        ['loc','qa','otherPub'].forEach(key => {
          (costs[sec][key] || []).forEach((v, ai) => {
            const i = ai - PRE_MONTHS;
            const n = parseFloat(v) || 0;
            if (i === now) pubThisMonth += n;
            if (i >= 0 && i <= now) pubYTD += n;
            if (i >= 0 && i <= fyEnd) pubForecast += n;
          });
        });
      });
    });
    // Royalties this month
    activeGames.forEach(g => {
      const aN = Array(NM).fill(0);
      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => { if (v !== null) aN[i] += v; });
      });
      const aR = Array(NM).fill(0);
      const costs2 = getCostState(CS, g);
      COST_FIELDS.forEach(({ key }) => {
        costs2.recoup[key].forEach((v, ai) => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) aR[i] += parseFloat(v) || 0; });
      });
      const dt2 = getDealTerms(DT, g);
      const { royaltyPayableRaw } = calcRoyalties(aN, aR, dt2);
      royThisMonth += royaltyPayableRaw[now] || 0;
    });

    // EBITDA for current month (matches P&L panel)
    // = Net Revenue - COS - People - All overhead categories
    // COS = dev advances + pub costs + marketing + royalties + dev payroll (matches PnLPanel totalCOS)
    const cosTotalNow = devThisMonth + mktThisMonth + pubThisMonth + royThisMonth + (peopleDevTotal?.[now] || 0);
    const grossProfitNow = monthlyRev - cosTotalNow;

    // Overheads = people OH + all non-belowEbitda overhead categories
    const ohCatTotalNow = (ohCategories || [])
      .filter(c => !c.belowEbitda)
      .reduce((s, cat) => {
        const totals = getCategoryMonthlyTotal ? getCategoryMonthlyTotal(cat.id) : [];
        return s + (totals[now] || 0);
      }, 0);
    const peopleTotalNow = (peopleOhTotal?.[now] || 0);
    const totalOHNow = peopleTotalNow + ohCatTotalNow;
    const ebitdaNow = grossProfitNow - totalOHNow;

    // Gross Profit by game for current month
    const gpByGame = activeGames.map(g => {
      let rev = 0, cos = 0;
      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        const v = calcPlatform(p.key, PS[g][p.key]).netRaw[now] ?? 0;
        rev += convert ? convert(v, fc, now) : v;
      });
      const costs = getCostState(CS, g);
      ['recoup','nonRecoup'].forEach(sec => {
        COST_FIELDS.forEach(({ key }) => {
          const ai = now + PRE_MONTHS;
          cos += parseFloat(costs[sec][key][ai]) || 0;
        });
      });
      // Add royalties for this game
      const aN = Array(NM).fill(0);
      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => { if (v !== null) aN[i] += v; });
      });
      const aR = Array(NM).fill(0);
      const costs2 = getCostState(CS, g);
      COST_FIELDS.forEach(({ key }) => {
        costs2.recoup[key].forEach((v, ai) => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) aR[i] += parseFloat(v) || 0; });
      });
      const dt2 = getDealTerms(DT, g);
      const { royaltyPayableRaw } = calcRoyalties(aN, aR, dt2);
      cos += royaltyPayableRaw[now] || 0;
      return { game: g, rev, cos, gp: rev - cos };
    });

    // Dynamic overhead categories for current month
    const ohCatRows = (ohCategories || []).filter(c => !c.belowEbitda).map(cat => {
      const totals = getCategoryMonthlyTotal ? getCategoryMonthlyTotal(cat.id) : [];
      const annual = Array.from({ length: 12 }, (_, i) => totals[i] || 0).reduce((a,v)=>a+v,0);
      return { label: cat.name, monthly: totals[now] || 0, annual };
    });

    const extTotal = devYTD + mktYTD + pubYTD;

    // Games signed/launched/recouped counts
    const gamesLaunched = activeGames.filter(g => {
      return PLATS.some(p => {
        const st = PS[g]?.[p.key];
        if (!st?.enabled) return false;
        const ea = st.launchEA !== '' ? parseInt(st.launchEA) : null;
        const fl = st.launchFL !== '' ? parseInt(st.launchFL) : null;
        const earliest = ea ?? fl;
        return earliest !== null && earliest <= now;
      });
    }).length;

    return {
      monthlyRev, monthlyOh, monthlyNet, runway,
      fyRevForecast, fyRevToDate, fyOhForecast, fyOhToDate,
      fyNetForecast: fyRevForecast - fyOhForecast,
      fyNetToDate:   fyRevToDate   - fyOhToDate,
      gamesTotal: games.length, gamesLaunched,
      devThisMonth, devYTD, devForecast,
      mktThisMonth, mktYTD, mktForecast,
      pubThisMonth, pubYTD, pubForecast,
      royThisMonth,
      extTotal,
      ebitdaNow, grossProfitNow, gpByGame, ohCatRows, ohCatTotalNow,
    };
  }, [games, PS, CS, DT, currentMonthIdx, now, fc, convert, peopleOhTotal, peopleDevTotal, ohCategories, getCategoryMonthlyTotal]);

  const monthLabel = startDate ? mlSafe(now, startDate) : `Month ${now + 1}`;
  const fyLabel    = startDate ? `FY${startDate.year}/${String(startDate.year + 1).slice(2)} Forecast vs Forecast-to-Date` : 'FY Forecast';

  const statusCell = (toDate, forecast) => {
    const p = forecast > 0 ? toDate / forecast : 0;
    if (p >= 0.9) return { label: 'GREEN', color: '#00c47a', bg: '#0d2818', border: '#1a7a3a' };
    if (p >= 0.6) return { label: 'AMBER', color: '#f5a623', bg: '#2a1a0d', border: '#8b5a1a' };
    return { label: 'RED', color: '#ff4d4d', bg: '#2a0d0d', border: '#8b1a1a' };
  };

  async function generateNarrative() {
    setGenLoading(true);
    try {
      const m = metrics;
      const prompt = `You are a games publishing CFO writing a brief P&L commentary for a board report.
Write 2-3 concise sentences covering the key financial highlights and concerns. Be specific with numbers. Professional tone.

Month: ${monthLabel}
Monthly Revenue (${fc}): ${fu(m.monthlyRev)}
Monthly Overhead (${fc}): ${fu(m.monthlyOh)}
Monthly Net (${fc}): ${fu(m.monthlyNet)}
FY Revenue: ${fu(m.fyRevToDate)} actual vs ${fu(m.fyRevForecast)} forecast (${m.fyRevForecast > 0 ? Math.round(m.fyRevToDate / m.fyRevForecast * 100) : 0}% of plan)
FY Overhead: ${fu(m.fyOhToDate)} actual vs ${fu(m.fyOhForecast)} forecast
FY Net: ${fu(m.fyNetToDate)} vs ${fu(m.fyNetForecast)} forecast
Dev Advances YTD: ${fu(m.devYTD)} of ${fu(m.devForecast)} forecast
Marketing YTD: ${fu(m.mktYTD)} of ${fu(m.mktForecast)} forecast

Write the commentary only. No headers, no bullet points.`;

      const res = await fetch('/api/generateNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const data = await res.json();
      if (data.error) {
        setNarrative('⚠ ' + data.error);
      } else {
        const txt = data.text || '';
      setNarrative(txt);
      supabase.from('app_settings').upsert({ org_id: orgId, key: 'pnl_narrative', value: txt }, { onConflict: 'org_id,key' });
      }
    } catch (e) {
      setNarrative('Error generating narrative. Please try again.');
    }
    setGenLoading(false);
  }

  const tblRow = (label, thisMonth, ytd, forecast, pctOfForecast, benchmark) => (
    <tr key={label}>
      <td style={{ fontSize: 12, color: 'var(--t2)', padding: '5px 10px' }}>{label}</td>
      <td style={{ fontSize: 12, textAlign: 'right', padding: '5px 10px', color: 'var(--t1)' }}>{fu(thisMonth)}</td>
      <td style={{ fontSize: 12, textAlign: 'right', padding: '5px 10px', color: 'var(--t1)' }}>{fu(ytd)}</td>
      <td style={{ fontSize: 12, textAlign: 'right', padding: '5px 10px', color: 'var(--t1)' }}>{fu(forecast)}</td>
      <td style={{ fontSize: 12, textAlign: 'right', padding: '5px 10px', color: 'var(--t3)' }}>{pctOfForecast}</td>
      {benchmark && <td style={{ fontSize: 11, padding: '5px 10px', color: 'var(--t3)' }}>{benchmark}</td>}
    </tr>
  );

  const thStyle = { fontSize: 9, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--t4)', padding: '6px 10px', background: 'var(--s1)', textAlign: 'right' };

  return (
    <div>
      {/* Top metric cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { value: fu(metrics.monthlyRev), label: 'Monthly Revenue', sub: 'portfolio total', color: 'var(--t0)' },
          { value: fu(metrics.devThisMonth + metrics.mktThisMonth + metrics.pubThisMonth + (metrics.royThisMonth||0) + (peopleDevTotal?.[now]||0)), label: 'Direct Costs', sub: 'this month', color: '#f5a623' },
          { value: fu(metrics.monthlyOh - metrics.devThisMonth - metrics.mktThisMonth - metrics.pubThisMonth), label: 'Overheads', sub: 'this month', color: '#f5a623' },
          { value: fu(metrics.ebitdaNow ?? metrics.monthlyNet), label: 'EBITDA', sub: (metrics.ebitdaNow ?? metrics.monthlyNet) >= 0 ? 'positive' : 'negative',
            color: (metrics.ebitdaNow ?? metrics.monthlyNet) >= 0 ? 'var(--green)' : '#ff4d4d' },
        ].map((m, i) => (
          <div key={i} style={{ flex: 1, minWidth: 140, padding: '16px 20px', background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: m.color, lineHeight: 1.1 }}>{m.value}</div>
            <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 5 }}>{m.label}</div>
            <div style={{ fontSize: 10, color: 'var(--t4)', marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14, marginBottom: 14 }}>
        {/* FY Forecast vs Forecast-to-Date */}
        <div style={{ background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', background: 'var(--s1)', borderBottom: '1px solid var(--e0)', fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--t3)' }}>
            {fyLabel}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left' }}>Metric</th>
                <th style={thStyle}>FY Forecast</th>
                <th style={thStyle}>To Date</th>
                <th style={thStyle}>Variance</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Revenue',     forecast: metrics.fyRevForecast, toDate: metrics.fyRevToDate },
                { label: 'Overhead',    forecast: metrics.fyOhForecast,  toDate: metrics.fyOhToDate  },
                { label: 'Net Income',  forecast: metrics.fyNetForecast, toDate: metrics.fyNetToDate  },
                { label: 'Games Signed',   forecast: games.length, toDate: games.length, isCount: true },
                { label: 'Games Launched', forecast: metrics.gamesLaunched, toDate: metrics.gamesLaunched, isCount: true },
              ].map(row => {
                const variance = row.toDate - row.forecast;
                const st = row.isCount ? null : statusCell(row.toDate, row.forecast);
                return (
                  <tr key={row.label} style={{ borderBottom: '1px solid var(--e0)' }}>
                    <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t2)' }}>{row.label}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>
                      {row.isCount ? row.forecast : fu(row.forecast)}
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>
                      {row.isCount ? row.toDate : fu(row.toDate)}
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: variance >= 0 ? 'var(--green)' : '#ff4d4d' }}>
                      {row.isCount ? (variance >= 0 ? '+' : '') + variance : (variance >= 0 ? '+' : '') + fu(variance)}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      {st && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 3, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                          {st.label}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Gross Profit by Game */}
        <div style={{ background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ padding: '8px 14px', background: 'var(--s1)', borderBottom: '1px solid var(--e0)', fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--t3)' }}>
            Gross Profit by Game — {monthLabel}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left' }}>Game</th>
                <th style={thStyle}>Net Revenue</th>
                <th style={thStyle}>Cost of Sales</th>
                <th style={thStyle}>Gross Profit</th>
                <th style={thStyle}>GP%</th>
              </tr>
            </thead>
            <tbody>
              {(metrics.gpByGame || []).map(row => {
                const gpm = row.rev > 0 ? Math.round(row.gp / row.rev * 100) : null;
                return (
                  <tr key={row.game} style={{ borderBottom: '1px solid var(--e0)' }}>
                    <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t2)' }}>{row.game}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.rev)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{row.cos > 0 ? `(${fu(row.cos)})` : '—'}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 500, color: row.gp >= 0 ? 'var(--green)' : 'var(--red)' }}>{fu(row.gp)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>{gpm !== null ? gpm + '%' : '—'}</td>
                  </tr>
                );
              })}
              {/* Unallocated — any overhead COS not in game-level */}
              {(() => {
                const allocRev = (metrics.gpByGame||[]).reduce((s,r)=>s+r.rev,0);
                const allocCos = (metrics.gpByGame||[]).reduce((s,r)=>s+r.cos,0);
                const totalCos = metrics.devThisMonth + metrics.mktThisMonth + metrics.pubThisMonth + (metrics.royThisMonth||0);
                const unallocCos = Math.max(0, totalCos - allocCos);
                if (unallocCos <= 0) return null;
                return (
                  <tr style={{ borderBottom: '1px solid var(--e0)' }}>
                    <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t3)', fontStyle: 'italic' }}>Unallocated</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>—</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>{`(${fu(unallocCos)})`}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>{fu(-unallocCos)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>—</td>
                  </tr>
                );
              })()}
              <tr style={{ borderTop: '2px solid var(--e1)', background: 'var(--s1)' }}>
                <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: 'var(--t1)' }}>Total</td>
                <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>{fu(metrics.monthlyRev)}</td>
                <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>{`(${fu(metrics.devThisMonth + metrics.mktThisMonth + metrics.pubThisMonth + (metrics.royThisMonth||0))})`}</td>
                <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: metrics.grossProfitNow >= 0 ? 'var(--green)' : 'var(--red)' }}>{fu(metrics.grossProfitNow)}</td>
                <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>
                  {metrics.monthlyRev > 0 ? Math.round(metrics.grossProfitNow / metrics.monthlyRev * 100) + '%' : '—'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Overhead Breakdown */}
        <div style={{ background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', background: 'var(--s1)', borderBottom: '1px solid var(--e0)', fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--t3)' }}>
            Overhead Breakdown
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left' }}>Category</th>
                <th style={thStyle}>This Month</th>
                <th style={thStyle}>FY Forecast</th>
              </tr>
            </thead>
            <tbody>
              {/* People always first */}
              {[
                { label: 'Staff', monthly: peopleOhTotal?.[now] || 0, annual: (peopleOhTotal || []).slice(0,12).reduce((a,v)=>a+v,0) },
                { label: 'Dev Payroll', monthly: peopleDevTotal?.[now] || 0, annual: (peopleDevTotal || []).slice(0,12).reduce((a,v)=>a+v,0) },
              ].map(row => (
                <tr key={row.label} style={{ borderBottom: '1px solid var(--e0)' }}>
                  <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t2)' }}>{row.label}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.monthly)}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.annual)}</td>
                </tr>
              ))}
              {/* Dynamic overhead categories */}
              {(metrics.ohCatRows || []).map(row => (
                <tr key={row.label} style={{ borderBottom: '1px solid var(--e0)' }}>
                  <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t2)' }}>{row.label}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.monthly)}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.annual)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--e1)', background: 'var(--s1)' }}>
                <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: 'var(--t1)' }}>Total Overhead</td>
                <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>{fu((peopleOhTotal?.[now]||0)+(peopleDevTotal?.[now]||0)+(metrics.ohCatTotalNow||0))}</td>
                <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>{fu(metrics.fyOhForecast)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Narrative */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={generateNarrative} disabled={genLoading}
          style={{ background: 'var(--blue-dim)', border: '1px solid var(--blue)', color: 'var(--blue)', borderRadius: 'var(--r)', padding: '6px 14px', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', cursor: genLoading ? 'wait' : 'pointer', fontFamily: 'var(--sans)' }}>
          {genLoading ? 'Generating…' : '✦ Generate Narrative'}
        </button>
      </div>
      <div style={{ background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', padding: '14px 18px', marginBottom: 14, minHeight: 48 }}>
        {narrative ? (
          <p style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.6, margin: 0 }}>
            <strong style={{ color: 'var(--t0)' }}>Headline: </strong>{narrative}
          </p>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--t4)', margin: 0, fontStyle: 'italic' }}>
            Click "Generate Narrative" for a board-ready P&L summary.
          </p>
        )}
      </div>

      {/* External Spend */}
      <div style={{ background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', overflow: 'hidden' }}>
        <div style={{ padding: '8px 14px', background: 'var(--s1)', borderBottom: '1px solid var(--e0)', fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--t3)' }}>
          External Spend by Category (All Games)
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Category','This Month','YTD','FY Forecast','% of Spend'].map((h,i) => (
                <th key={h} style={{ ...thStyle, textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Dev Advances',           tm: metrics.devThisMonth, ytd: metrics.devYTD, fc2: metrics.devForecast },
              { label: 'Marketing',              tm: metrics.mktThisMonth, ytd: metrics.mktYTD, fc2: metrics.mktForecast },
              { label: 'Publishing (Loc/QA/Port)', tm: metrics.pubThisMonth, ytd: metrics.pubYTD, fc2: metrics.pubForecast },
            ].map(row => {
              const pct = metrics.extTotal > 0 ? Math.round(row.ytd / metrics.extTotal * 100) + '%' : '—';
              return (
                <tr key={row.label} style={{ borderBottom: '1px solid var(--e0)' }}>
                  <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--t2)' }}>{row.label}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.tm)}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.ytd)}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t1)' }}>{fu(row.fc2)}</td>
                  <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>{pct}</td>
                </tr>
              );
            })}
            <tr style={{ borderTop: '2px solid var(--e1)', background: 'var(--s1)' }}>
              <td style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: 'var(--t1)' }}>Total External</td>
              <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>{fu(metrics.devThisMonth + metrics.mktThisMonth + metrics.pubThisMonth)}</td>
              <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>{fu(metrics.extTotal)}</td>
              <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--blue)' }}>{fu(metrics.devForecast + metrics.mktForecast + metrics.pubForecast)}</td>
              <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
